import React, { useEffect, useState } from "react";
import { useAppSelector, useAppDispatch } from "hooks/redux";
import { StatusCode } from "api/protocols";
import { useTranslation } from "react-i18next";
import { IPutTeeSheetSettings, PutFacilityTeeSheetSettings } from "api/rpc/2024-04/facilityAdmin/facility/settings";
import { updateFacilityTeeSheetSettings } from "redux/actions/facility";
import { showError, showSuccess } from "redux/actions/ui";
import Page from "components/page/Page";
import ScrollTabsLayout, { TSection } from "components/scrollTabsLayout/ScrollTabsLayout";
import FacilityRegisterSettings from "./sections/FacilityRegisterSettings";
import FacilityAccountDefaultsSettings from "./sections/FacilityAccountDefaultsSettings";
import FacilityQRCodeSettings from "./sections/FacilityQRCodeSettings";
import FacilityNotificationSettings from "./sections/FacilityNotificationSettings";
import { isEqualWith } from "lodash";
import FacilityTableServiceSettings from "./sections/FacilityTableServiceSettings";
import FacilityOnlineStoreSettings from "./sections/FacilityOnlineStoreSettings";
import FacilityInvoiceSettings from "./sections/FacilityInvoiceSettings";
import FacilityGiftCardSettings from "./sections/FacilityGiftCardSettings";
import { GetQuickSaleTable, PutTable } from "api/rpc/2024-04/facilityAdmin/tables/tables";
import FacilityAccessControlSettings from "./sections/FacilityAccessControlSettings";

export interface IQuickStaleTableState {
  id: number;
  title: string;
  originalTitle: string;
}

export default function FacilitySettings() {
  const dispatch = useAppDispatch();
  const { facilityStore } = useAppSelector(store => store);
  const [facilitySettings, setFacilitySettings] = useState<IPutTeeSheetSettings>(undefined);
  const [stateBeforeChanges, setStateBeforeChanges] = useState<IPutTeeSheetSettings>(undefined);
  const { t } = useTranslation();

  const [quickSaleTableState, setQuickSaleTableState] = useState<IQuickStaleTableState>({
    id: null,
    title: "",
    originalTitle: "",
  });

  useEffect(() => {
    if (facilityStore.teeSheetSettings) {
      const settings = {
        ...facilityStore.teeSheetSettings,
        gift_card_expiry: facilityStore.teeSheetSettings.gift_card_expiry ?? "no_expiry",
        gift_card_expiry_years: facilityStore.teeSheetSettings.gift_card_expiry_years ?? 5,
      };
      setFacilitySettings(settings);
      setStateBeforeChanges(settings);
      void loadQuickSaleTable();
    }
  }, [facilityStore.teeSheetSettings]);

  async function loadQuickSaleTable() {
    const tableRes = await GetQuickSaleTable({}, false);

    if (tableRes.status !== StatusCode.OK) {
      dispatch(showError("Error loading quick sale table"));
      return;
    }

    setQuickSaleTableState({
      id: tableRes.data?.id,
      title: tableRes.data?.title,
      originalTitle: tableRes.data?.title,
    });
  }

  async function updateQuickSale() {
    const updateRes = await PutTable({ id: quickSaleTableState?.id, title: quickSaleTableState?.title }, true);

    if (updateRes.status !== StatusCode.OK) {
      dispatch(showError("Error updating quick sale table"));
      return;
    }

    dispatch(showSuccess("Quick Sale title updated successfully"));
    setQuickSaleTableState({
      id: updateRes.data[0]?.id,
      title: updateRes.data[0]?.title,
      originalTitle: updateRes.data[0]?.title,
    });
  }

  const wasQuickTitleChanged = quickSaleTableState.title !== quickSaleTableState.originalTitle;

  async function saveSettings() {
    console.log(facilitySettings);
    if (!isEqualWith(stateBeforeChanges, facilitySettings)) {
      const settingsRes = await PutFacilityTeeSheetSettings(
        {
          ...facilitySettings,
        },
        true,
      );

      if (settingsRes.status !== StatusCode.OK) {
        dispatch(showError(typeof settingsRes?.data === "string" ? settingsRes?.data : t("Error updating facility settings")));
        return;
      }

      dispatch(showSuccess(t("Facility settings updated successfully")));
      setStateBeforeChanges(settingsRes.data);
      void dispatch(updateFacilityTeeSheetSettings(settingsRes.data, true));
    }

    if (wasQuickTitleChanged) {
      void updateQuickSale();
    }
  }

  function updateState<K extends keyof IPutTeeSheetSettings>(key: K, value: IPutTeeSheetSettings[K]) {
    setFacilitySettings(prev => ({ ...prev, [key]: value }));
  }

  function updateTableState(title: string) {
    setQuickSaleTableState(prev => ({ ...prev, title: title }));
  }

  function unsavedChangesExist() {
    if (stateBeforeChanges === undefined) {
      if (facilitySettings !== undefined) {
        setStateBeforeChanges({ ...facilitySettings });
      }
      return false;
    }

    return !isEqualWith(stateBeforeChanges, facilitySettings) || wasQuickTitleChanged;
  }

  function cancelUnsavedChanges() {
    setFacilitySettings({ ...stateBeforeChanges });
  }

  const primaryAction = {
    content: t("Save"),
    action: saveSettings,
  };

  const settingsSections: Array<TSection> = [
    {
      id: "register",
      label: t("Register"),
      content: <FacilityRegisterSettings state={facilitySettings} updateState={updateState} />,
      keywords: [
        "void note required",
        "blind close",
        "round change due",
        "cash change",
        "stack line items",
        "show outstanding balance",
        "signature line on receipt",
        "receipt name",
        "receipt member number",
        "vendor on product card",
        "inventory on product card",
        "product images",
        "default pos view",
        "review player details modal",
        "customer type",
        "register controls",
        "display options",
      ],
    },
    {
      id: "gift-cards",
      label: t("Gift Cards"),
      content: <FacilityGiftCardSettings state={facilitySettings} updateState={updateState} />,
      keywords: [
        "gift card",
        "gift cards",
        "auto select generate gift card",
        "generate gift card",
        "gift card expiry",
        "expiry",
        "expiration",
        "no expiration",
        "one year",
        "end of year",
        "custom years",
      ],
    },
    {
      id: "table-service",
      label: t("Table service"),
      content: (
        <FacilityTableServiceSettings
          state={facilitySettings}
          table={quickSaleTableState}
          updateState={updateState}
          updateTableState={updateTableState}
        />
      ),
      keywords: [
        "table service",
        "clock-in required",
        "send kitchen chits when leaving a table",
        "quick sale table title",
      ],
    },
    {
      id: "account-defaults",
      label: t("Account defaults"),
      content: <FacilityAccountDefaultsSettings state={facilitySettings} updateState={updateState} />,
      keywords: [
        "account defaults",
        "auto pay balance",
        "payment day",
        "send statements",
        "default accounting reference",
      ],
    },
    {
      id: "receipt-configuration",
      label: t("Receipt configuration"),
      content: <FacilityQRCodeSettings state={facilitySettings} updateState={updateState} />,
      keywords: ["receipt configuration", "receipt url", "url description", "qr code", "preview"],
    },
    {
      id: "notifications",
      label: t("Notifications and configurations"),
      content: <FacilityNotificationSettings state={facilitySettings} updateState={updateState} />,
      keywords: [
        "online store notifications",
        "tee time confirmations",
        "reservation confirmations",
        "notification email",
        "confirmation email",
      ],
    },
    {
      id: "online-store",
      label: t("Online store"),
      content: <FacilityOnlineStoreSettings state={facilitySettings} updateState={updateState} />,
      keywords: ["online store", "all products category"],
    },
    {
      id: "invoice",
      label: t("Invoice"),
      content: <FacilityInvoiceSettings state={facilitySettings} updateState={updateState} />,
      keywords: ["invoice", "invoice terms"],
    },
    {
      id: "access-control",
      label: t("Access Control"),
      content: <FacilityAccessControlSettings state={facilitySettings} updateState={updateState} />,
      keywords: ["restrict staff login by location", "radius", "authentication"],
    },
  ];

  return (
    <Page
      title={t("Facility Settings")}
      primaryAction={primaryAction}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: saveSettings,
        onCancel: cancelUnsavedChanges,
      }}
    >
      <ScrollTabsLayout
        showSectionTitles
        sections={settingsSections}
        emptyState={{
          title: t("Setting Not Found"),
          content: t("No matching setting found. Please readjust your search and try again."),
        }}
      />
    </Page>
  );
}
